import styled, { css } from "styled-components"
import { down, up } from "styled-breakpoints"
import { columns } from "@constants/layout"
import colors from "@constants/colors"
import { subtext } from "@constants/typography"
import { IconWrapper } from "@components/icons/utils"
import { Tabs, Tab } from "@mui/material"

// ============================================================================
// VehicleSection.tsx styled components
// ============================================================================

export const SectionContainer = styled.div`
  margin-bottom: 48px;

  ${down("tablet")} {
    margin-bottom: 0;
  }
`

export const SectionTitle = styled.h2`
  color: ${colors.red};
  text-decoration: underline;
  font-family: "Jost", sans-serif;
  font-size: 18px;
  font-weight: 600;
  text-transform: uppercase;
  margin: 0 0 24px 0;
  padding: 0;

  ${down("tablet")} {
    font-size: 16px;
    margin-bottom: 20px;
  }

  ${down("mobile")} {
    font-size: 14px;
    margin-bottom: 16px;
  }
`

export const Filters = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  /**
   * Optimized layout for medium screens (820px - 1440px):
   * - Use nowrap to force filters into one row
   * - Reduce gap from 20px to 12px to save horizontal space
   * - Stretch items to fill available space evenly
   */
  ${up("tabletLarge")} {
    flex-wrap: nowrap;
    gap: 12px;
    align-items: stretch;
  }

  /**
   * Revert to original layout on desktop (1440px+):
   * - Allow wrapping (original behavior)
   * - Restore original 20px gap
   * - Use flex-start alignment (original behavior)
   */
  ${up("desktop")} {
    flex-wrap: wrap;
    gap: 20px;
    align-items: flex-start;
  }

  ${down("tablet")} {
    flex-direction: column;
    gap: 10px;
  }
`

export const Filter = styled.div<{ disabled?: boolean }>`
  min-width: 150px;
  font-family: "Jost", sans-serif;
  flex-grow: 0;
  flex-shrink: 0;

  /**
   * Optimized layout for medium screens (820px - 1440px):
   * - Use flex-grow: 1 to distribute filters evenly across available space
   * - Use flex-basis: 0 to ensure equal distribution
   * - This helps all filters fit in one row by filling available space
   */
  ${up("tabletLarge")} {
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 0;
  }

  /**
   * Revert to original fixed-width behavior on desktop (1440px+):
   * - Use fixed min-width (original behavior)
   * - No flex-grow to maintain original sizing
   */
  ${up("desktop")} {
    flex-grow: 0;
    flex-shrink: 0;
    flex-basis: auto;
  }

  ${down("tablet")} {
    min-width: 0;
    width: 100%;
    flex-grow: 1;
  }

  ${({ disabled }) => css`
    pointer-events: ${disabled ? "none" : "all"};

    [class*="SelectInput__Container"] {
      background-color: ${disabled ? colors.mediumGray : colors.lightestGray};
    }
  `}

  [class*="SelectInput__Container"] {
    padding: 0 8px;
    height: 36px;
    width: 100%;

    ${down("tablet")} {
      height: 50px;
    }

    ${down("mobile")} {
      height: 48px;
      padding: 0 12px;
    }
  }

  [class*="MuiInput-formControl"] {
    margin-top: 16px;
    height: 16px;
    font-size: 14px;

    ${down("tablet")} {
      margin-top: 22px;
    }
  }

  [class*="MuiInputLabel-root"] {
    font-family: "Jost", sans-serif;
    font-weight: 400;
    text-transform: uppercase;
    line-height: 16px;
    transform: translate(0, 10px) scale(1);

    ${down("tablet")} {
      transform: translate(0, 18px) scale(1);
    }
  }

  .MuiInputLabel-shrink {
    transform: translate(0, 4px) scale(0.6) !important;
  }

  .MuiNativeSelect-icon {
    top: 0px;
  }
`

export const BuildButton = styled.button`
  background: #db2627;
  color: ${colors.snow};
  min-width: 150px;
  text-transform: uppercase;
  border: 0;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: 0.3s ease-out;
  height: 36px;
  font-family: "Jost", sans-serif;

  ${up("tabletLarge")} {
    flex-shrink: 0;
    flex-grow: 0;
  }

  ${up("desktop")} {
    flex-shrink: 0;
    flex-grow: 0;
  }

  ${down("tablet")} {
    width: 100%;
    min-width: 0;
    padding: 15px;
    height: 50px;
  }

  ${down("mobile")} {
    height: 48px;
    font-size: 13px;
  }

  &:hover:not(:disabled) {
    background: #b81e1f;
    color: ${colors.snow};
  }

  &:disabled {
    background: ${colors.mediumGray};
    cursor: not-allowed;
    opacity: 0.6;
  }
`

export const PopularBuildsContainer = styled.div`
  margin-top: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  ${down("tablet")} {
    flex-direction: column;
    align-items: flex-start;
  }
`

export const PopularBuildsLabel = styled.div`
  font-family: "Jost", sans-serif;
  font-size: 14px;
  color: ${colors.slateDark};
  text-transform: capitalize;
  white-space: nowrap;

  ${down("tablet")} {
    font-size: 13px;
  }

  ${down("mobile")} {
    font-size: 12px;
    white-space: normal;
    color: ${colors.snow};
  }
`

export const PopularBuildsList = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  flex-wrap: nowrap;
  align-items: center;

  ${down("tablet")} {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    margin-top: 12px;
    flex-wrap: wrap;
  }
`

export const PopularBuildButton = styled.button<{
  variant?: "classic" | "modern"
}>`
  && {
    background: ${({ variant }) =>
      variant === "classic" ? colors.red : "#000000"} !important;
    color: ${colors.snow} !important;
    min-width: auto !important;
    text-transform: none !important;
  }
  padding: 10px 20px;
  border: 0;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: 0.3s ease-out;
  font-family: "Jost", sans-serif;
  white-space: nowrap;

  ${down("tablet")} {
    width: 100%;
  }

  &:hover {
    background: ${({ variant }) =>
      variant === "classic" ? colors.red : "#000000"} !important;
    color: ${colors.snow} !important;
    opacity: 0.9;
    transform: translateY(-1px);
  }
`

// ============================================================================
// CarSelector.tsx styled components
// ============================================================================

export const Container = styled.div`
  width: 100%;
  // width: fit-content;
  padding: 36px ${columns(4)};
  background-color: ${colors.snow};
`

export const Body = styled.div<{
  bgColor: string
}>`
  ${({ bgColor }) => css`
    display: block;
    font-family: "Jost", sans-serif;
    background-color: ${bgColor ? bgColor : `#000000`};

    ${down("tablet")} {
      background: rgb(0, 0, 0);
      background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 1) 0%,
        rgba(0, 0, 0, 0) 100%
      );
      background: rgb(0, 0, 0);
      background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 1) 0%,
        rgba(0, 0, 0, 0) 100%
      );
      padding: 30px;
    }
  `}

  button {
    background: #ff1c24;
    color: #fff;
    min-width: 150px;
    text-transform: uppercase;
    border: 0;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: 0.6s ease-out;

    ${down("tablet")} {
      width: 100%;
      padding: 15px;
    }

    &:hover {
      color: #ff1c24;
      background: #fff;
    }

    &:disabled {
      background: ${colors.mediumGray};
      cursor: not-allowed;
    }
  }
`

export const Label = styled.div<{
  fontColor: string
}>`
  ${({ fontColor }) => css`
    color: ${fontColor ? fontColor : `#ffffff`};
    margin-bottom: 15px;
    text-transform: uppercase;
    font-family: "Jost", sans-serif;

    ${down("tablet")} {
      margin-bottom: 0;
      text-shadow: 0 0 10px black;
    }

    > div {
      font-size: 15px;

      ${down("tablet")} {
        text-align: center;
        font-size: 20px;
        font-weight: 600;
      }
    }

    > h1 {
      font-size: 25px;
      line-height: 25px;
      font-weight: 400;
      margin: 0;

      ${down("tablet")} {
        display: none;
      }
    }
  `}
`

export const Footer = styled.div`
  ${subtext}
  font-weight: normal;
  align-items: center;
  justify-content: start;
  display: flex;
  color: ${colors.slateDark};

  ${IconWrapper} {
    opacity: 0.4;
    width: 20px;
    height: 20px;
    margin-right: 20px;
    border: 1.5px solid ${colors.slateLight};
    border-radius: 50%;
  }
`

export const TabsContainer = styled.div<{ width?: string }>`
  margin-bottom: 32px;
  /* border-bottom: 1px solid ${colors.silver}; */
  ${({ width }) => width && `width: ${width};`}

  ${down("tablet")} {
    margin-bottom: 0;
    /* border-bottom: none; */
  }
`

export const StyledTabs = styled(Tabs)`
  && {
    min-height: 0;
    position: relative;
  }

  &&::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background-color: ${colors.red};
    z-index: 0;
  }

  &.MuiTabs-root {
    min-height: 0;
  }

  .MuiTabs-indicator {
    display: none;
  }

  .MuiTabs-flexContainer {
    gap: 0;
    flex-wrap: nowrap;
    position: relative;
    z-index: 1;

    ${down("mobile")} {
      width: 100%;
    }
  }
`

export const StyledTab = styled(Tab)`
  && {
    font-family: "Jost", sans-serif;
    font-size: 14px;
    font-weight: 500;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.65);
    padding: 12px 24px;
    margin-right: 2px;
    min-height: auto;
    min-width: auto;
    text-align: left;
    align-items: flex-start;
    opacity: 1;
    position: relative;
    z-index: 1;
    white-space: nowrap;
    flex-shrink: 0;
    letter-spacing: 0;
    background: #fafafa;
    border-radius: 8px 8px 0 0;
    border: 1px solid #d9d9d9;
    border-bottom: none;
    margin-bottom: 1px;
    transition: all 0.2s;
  }

  ${down("tablet")} {
    && {
      font-size: 16px;
      margin-right: 2px;
      padding: 10px 20px;
      width: auto;
      flex: 0 0 auto;
      min-width: auto;
      max-width: none;
    }
  }

  ${down("mobile")} {
    && {
      font-size: 14px;
      margin-right: 2px;
      padding: 8px 16px;
      width: auto !important;
      flex: 0 0 auto !important;
      min-width: auto !important;
      max-width: none !important;
    }
  }

  &&.Mui-selected {
    color: ${colors.red};
    background: #ffffff;
    border: 1px solid ${colors.red};
    border-bottom: none;
    margin-bottom: 0;
    z-index: 2;
  }

  &&:hover:not(.Mui-selected) {
    color: ${colors.red};
  }

  .MuiTab-wrapper {
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    font-weight: 500;
  }

  span {
    white-space: nowrap;
    display: inline;
    font-weight: 500;
  }
`
